import React from 'react';
import {
  Wrap,
  Product,
  Typography,
  SkeletonLoader,
  LocationDropdown,
  NothingToDisplay,
  Button,
} from '../../components/index';
import { FreeProductsReducer } from '../../interfaces';
import * as _ from 'lodash';
import { Row, Col } from 'react-styled-flexboxgrid';
import {
  useResponsivity,
  renderCdnUrl,
  scrollToTop,
} from '../../helpers/helpers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import store from 'store2';
import Styled from './FreeProducts.style';

export interface FreeProductsProps {
  page: FreeProductsReducer;
  getFreeProducts: Function;
  addToOrder: Function;
  removeFromOrder: Function;
  getAddresses: Function;
  listLocation: any;
  user: any;
  getPropertyDetails: Function;
  setMeta: Function;
}

const FreeProducts: React.FC<FreeProductsProps> = (props) => {
  const {
    page,
    getFreeProducts,
    addToOrder,
    removeFromOrder,
    getAddresses,
    listLocation,
    getPropertyDetails,
    user,
    setMeta,
  } = props;

  const navigate = useNavigate();
  const isMobile = useResponsivity();
  const [t] = useTranslation('common');

  const dataLocation = _.get(listLocation, 'data.data');
  const dataLength = _.get(dataLocation, 'length');
  const initialPropertyId = Number(store.get('property')) || 0;

  const [propertyId, setPropertyId] = React.useState<number>(initialPropertyId);

  const [isChangingLocation, setIsChangingLocation] =
    React.useState<boolean>(true);

  const [productsPropertyId, setProductsPropertyId] = React.useState<
    number | null
  >(null);

  const findLocation = _.find(dataLocation, (location) => {
    return location.id === propertyId;
  });

  const postcode = findLocation?.postcode;

  const wholesalerSlug = _.get(user, 'data.data.wholesaler_slug');

  const loadedPropertyId = _.get(user, 'data.data.property_id');

  const {
    error = false,
    loading = false,
    done = false,
  } = _.get(page, 'products.status');

  const { groups = [] } = page.products;
  const { basket } = props.page;

  const renderMapOptions =
    dataLocation &&
    dataLocation.map((el) => ({
      value: el.id,
      label: el.property_name,
    }));

  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    getPropertyDetails(propertyId);
  }, [propertyId, getPropertyDetails]);

  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    if (!loadedPropertyId || loadedPropertyId !== propertyId) {
      return;
    }

    if (!wholesalerSlug) {
      setProductsPropertyId(null);
      setIsChangingLocation(false);
      return;
    }

    setProductsPropertyId(propertyId);

    getFreeProducts(wholesalerSlug);

    if (postcode) {
      getAddresses(postcode);
    }
  }, [
    propertyId,
    loadedPropertyId,
    wholesalerSlug,
    postcode,
    getFreeProducts,
    getAddresses,
  ]);

  React.useEffect(() => {
    if (!isChangingLocation) {
      return;
    }

    if (productsPropertyId !== propertyId) {
      return;
    }

    if (loading) {
      return;
    }

    if (done || error) {
      setIsChangingLocation(false);
    }
  }, [
    loading,
    done,
    error,
    propertyId,
    productsPropertyId,
    isChangingLocation,
  ]);

  const singleProduct = (id, groupIndex) => {
    const { data, groups } = props.page.products;

    const elementProps = _.get(data, `entities.products[${id}]`);

    if (!elementProps) {
      return null;
    }

    const count = _.get(basket, `[${id}].total`, 0);

    const { limit, limitRule } = groups[groupIndex];

    const inGroup = groups[groupIndex].products;

    const basketIds = Object.keys(basket);

    const productInBasket = _.reduce(
      inGroup,
      (total, productId) => {
        if (_.includes(basketIds, productId.toString())) {
          return total + 1;
        }

        return total;
      },
      0,
    );

    const itemsInBasket = _.reduce(
      inGroup,
      (total, productId) => {
        if (_.includes(basketIds, productId.toString())) {
          const countItem = _.get(basket, `[${productId}].total`, 0);

          return total + countItem;
        }

        return total;
      },
      0,
    );

    const getMax =
      limit < inGroup.length
        ? productInBasket === limit
        : productInBasket > limit;

    const maxItemsInGroup = limit === 0 ? false : getMax;

    const maxInGroup = !(limit > itemsInBasket);

    const inBasket = _.includes(basketIds, id.toString());

    const hasImage = _.get(elementProps, 'images.path');

    const image = hasImage
      ? `https://cdn.savewatersavemoney.co.uk/cache/product_page${hasImage}`
      : renderCdnUrl('files/gwf/no-image.png');

    return (
      <Product
        description={
          elementProps.descriptionTranslationKey
            ? t(elementProps.descriptionTranslationKey)
            : elementProps.freePackDescription
        }
        maxQuantity={elementProps.maxperhousehold}
        maxInGroup={
          limitRule === 'mix-and-match'
            ? maxInGroup
            : maxItemsInGroup && !inBasket
        }
        key={elementProps.id}
        image={image}
        addToOrder={addToOrder}
        removeFromOrder={removeFromOrder}
        title={
          elementProps.titleTranslationKey
            ? t(elementProps.titleTranslationKey)
            : elementProps.title
        }
        id={elementProps.id}
        count={count}
        stockCheck={elementProps.state}
        limitedOffer={elementProps.limitedOffer}
      />
    );
  };

  const renderProducts = (productGroups) => {
    if (productGroups.length === 0) {
      return null;
    }

    return _.map(productGroups, (group, groupIndex) => {
      const elements = _.map(group.products, (id) =>
        singleProduct(id, groupIndex),
      );

      return (
        <Row key={groupIndex}>
          <Col xs={12} sm={12} md={4}>
            <Typography
              size="3"
              style={{
                fontSize: !isMobile ? '22px' : '20px',
              }}
              text={group.name}
            />
          </Col>

          <Row>{elements}</Row>
        </Row>
      );
    });
  };

  const pageTitle = t('portal.free_products.page_title');

  const pageDisclaimer = t('portal.free_products.page_disclaimer');

  const renderContent = () => {
    if (!wholesalerSlug) {
      return (
        <Col md={12} xs={12} sm={12}>
          <NothingToDisplay
            title="There are no products available"
            subtitle="Please come back later to check if something has changed"
          />
        </Col>
      );
    }

    if (error) {
      return (
        <Col md={12} xs={12} sm={12}>
          <NothingToDisplay
            title="There are no products available"
            subtitle="Please come back later to check if something has changed"
          />
        </Col>
      );
    }

    if (groups.length === 0) {
      return (
        <Col md={12} xs={12} sm={12}>
          <NothingToDisplay
            title="There are no products available"
            subtitle="Please come back later to check if something has changed"
          />
        </Col>
      );
    }

    return (
      <Row>
        <Col md={12} sm={12} xs={12}>
          {renderProducts(groups)}
        </Col>

        <Col md={12} sm={12} xs={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: !isMobile ? 'row' : 'column',
            }}
          >
            <Typography
              size="p"
              text={pageDisclaimer}
              style={{
                fontSize: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
              }}
            />

            <Button
              onClick={() => {
                scrollToTop();
                navigate('/free-products/checkout');
              }}
              style={{
                height: '50px',
              }}
            >
              {t('portal.free_products.page_checkout')}
            </Button>
          </div>
        </Col>
      </Row>
    );
  };

  const shouldShowSkeleton = isChangingLocation || (loading && !done);

  return (
    <Wrap>
      <Row>
        <Col md={8} xs={12} sm={12}>
          <Typography
            weight={700}
            size="1"
            text={pageTitle}
            style={{
              marginBottom: '16px',
              fontSize: !isMobile ? '40px' : '28px',
              color: '#0b2641',
            }}
          />
        </Col>

        {dataLength <= 1 ? null : (
          <Col md={4} xs={12} sm={12}>
            <Styled.DropdownWrap>
              <LocationDropdown
                value={String(propertyId)}
                onChange={(e) => {
                  const id = Number(e.target.value);

                  setIsChangingLocation(true);

                  setProductsPropertyId(null);

                  setPropertyId(id);

                  store('property', id);

                  setMeta(id);
                }}
                options={renderMapOptions}
              />
            </Styled.DropdownWrap>
          </Col>
        )}

        <Col md={12} xs={12} sm={12}>
          {shouldShowSkeleton ? <SkeletonLoader /> : renderContent()}
        </Col>
      </Row>
    </Wrap>
  );
};

export default FreeProducts;
